/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React from 'react';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { getTemplateLabelEnums } from '@/utils/evalTemplates';
import { sendEvalPromptToKahu } from '@/utils/sendEvalPromptToKahu';
import EvalBadge from '@/ux-common/src/components/evals/eval-badge';
import Icons from '@/ux-common/src/icons/icons';
import { EvalResults } from '@/ux-common/src/types/api';
import { sizeStyles } from '@/ux-common/src/utils/constants';

import { cn } from '@/lib/utils';
import { useEvals } from '@/hooks/useEvals';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  copyTraceId,
  truncateTraceId,
} from '../display-page/traces/traceUtils';
import { Icons as PortalIcons } from '../icons';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

type SequenceSeparatorProps = {
  id: string;
  traceId?: string;
  label: string;
  showDropdown: boolean;
  traceEvalArr?: EvalResults[];
  factName?: string;
  badgeFactName?: string;
  startTime?: string;
  endTime?: string;
  appName?: string;
  workflowName?: string;
  subtype?: string;
  setEvaluateOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  handleIdSpecificEvalChange?: (
    id: string,
    source: 'trace' | 'conversation'
  ) => void;
  filterStartTime?: string | null;
  filterEndTime?: string | null;
};

const SequenceSeparator: React.FC<SequenceSeparatorProps> = ({
  id,
  traceId,
  label,
  showDropdown,
  traceEvalArr,
  factName,
  badgeFactName,
  startTime,
  endTime,
  appName,
  workflowName,
  subtype,
  setEvaluateOpen,
  handleIdSpecificEvalChange,
  filterStartTime,
  filterEndTime,
}) => {
  const { messages } = useLanguage();
  const { state, evaluateActions } = useEvals();
  const { evalTemplates } = state;
  const { traceEvalLoading } = state;
  const promptSequenceConstants = messages?.PromptSequenceView;
  const langData = messages;
  const tracesNavigationOptions =
    label !== promptSequenceConstants?.General?.spanId
      ? [
          {
            key: 'copyTraceId',
            label: langData?.PromptSequenceView?.General?.copyTraceId,
            icon: PortalIcons.Copy('h-4 w-4 text-[#257C9D]'),
          },
          {
            key: 'searchByTraceId',
            label: langData?.PromptSequenceView?.General?.searchTraceById,
            icon: Icons.Search('h-4 w-4 stroke-[#257C9D]'),
          },
          {
            key: 'evaluateTrace',
            label: langData?.PromptSequenceView?.General?.evaluateTrace,
            icon: Icons.EvaluateIcon('h-4 w-4 text-[#257C9D]'),
          },
        ]
      : [
          {
            key: 'evaluateSpan',
            label: langData?.PromptSequenceView?.General?.evaluateTrace,
            icon: Icons.EvaluateIcon('h-4 w-4 text-[#257C9D]'),
          },
        ];
  const router = useRouter();

  const handleClick = (key: string) => {
    switch (key) {
      case 'searchByTraceId':
        router.push(`traces?trace_id=${id}`);
        break;
      case 'copyTraceId':
        copyTraceId(id, promptSequenceConstants?.General?.traceIdCopied);
        break;
      case 'evaluateTrace':
      case 'evaluateSpan':
        setEvaluateOpen && setEvaluateOpen(true);
        handleIdSpecificEvalChange &&
          handleIdSpecificEvalChange(
            traceId || '', //pass traceID for span eval also.
            'trace'
          );
        break;
      default:
        break;
    }
  };
  return (
    <div className={cn(' flex items-center justify-center')}>
      <div className="flex items-center justify-center gap-[6px]">
        <div className="flex w-fit items-center justify-center gap-1 py-1 text-body-sm font-medium text-form-input-disabled hover:text-body-secondary">
          <span>{label}:</span>
          {showDropdown ? (
            <DropdownMenu modal={false}>
              <TooltipProvider>
                <Tooltip>
                  <DropdownMenuTrigger asChild>
                    <TooltipTrigger asChild>
                      <span className="cursor-pointer">
                        {truncateTraceId(id)}
                      </span>
                    </TooltipTrigger>
                  </DropdownMenuTrigger>
                  <TooltipContent
                    side="bottom"
                    className="rounded p-[9px] text-[11px] leading-4 tracking-[2%] text-white"
                  >
                    <p>{label}</p>
                    <span>{id}</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DropdownMenuContent
                align="end"
                className="min-w-44 cursor-pointer rounded-none bg-white p-0 text-h5 text-black"
              >
                {tracesNavigationOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.key}
                    className="flex cursor-pointer items-center gap-2 p-2 hover:bg-gray-100"
                    onClick={() => handleClick(option.key)}
                  >
                    {option.icon && (
                      <span className="flex-shrink-0">{option.icon}</span>
                    )}
                    <span>{option.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="group flex items-center justify-center">
                    <span>{truncateTraceId(id)}</span>
                    {subtype && subtype.length > 0 && (
                      <>
                        <Separator
                          className="mx-[6px] h-full min-h-3 border border-line-tertiary"
                          orientation="vertical"
                        />

                        <span className="text-[#8C9EA1]">{subtype}</span>

                        <Separator
                          className="mx-[6px] h-full min-h-3 border border-line-tertiary"
                          orientation="vertical"
                        />
                      </>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="rounded p-[9px] text-[11px] leading-4 tracking-[2%] text-white"
                >
                  <p>{label}</p>
                  <span>{id}</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {factName && factName !== 'traces' && traceEvalLoading ? (
          <Skeleton
            className={cn(
              'animate-pulse-custom w-16 rounded-full bg-[#8C9EA1]',
              sizeStyles['compact']
            )}
          />
        ) : traceEvalArr && traceEvalArr.length > 0 ? (
          <>
            {traceEvalArr.map((traceEval) => (
              <EvalBadge
                key={`${traceEval.fact_id}_${traceEval.eval_timestamp}`}
                size="compact"
                evalObj={traceEval}
                submitManualLabel={evaluateActions?.submitManualLabel}
                createEvalsJobs={evaluateActions?.createEvalsJobs}
                factName={badgeFactName}
                startTime={startTime}
                endTime={endTime}
                appName={appName || ''}
                workflowName={traceEval.workflow_name || workflowName}
                templateEnums={getTemplateLabelEnums(
                  evalTemplates,
                  traceEval.eval_name
                )}
                sendEvalPromptToKahu={sendEvalPromptToKahu}
                filterStartTime={filterStartTime}
                filterEndTime={filterEndTime}
              />
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default SequenceSeparator;
